import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Search, Edit, Trash2, UserCheck, Eye, MessageSquare, BookOpen, GraduationCap, Users } from "lucide-react";

const SUBJECTS = ["Quran", "Arabic", "Islamic Studies", "Hadith", "Fiqh", "Seerah"];

const Teachers = () => {
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [viewingProfile, setViewingProfile] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    subjects: [],
    assigned_classes: [],
    salary: "",
    role: "teacher",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teachersRes, classesRes] = await Promise.all([
        axios.get(`${API}/teachers`),
        axios.get(`${API}/classes`),
      ]);
      setTeachers(teachersRes.data);
      setClasses(classesRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchTeacherProfile = async (teacherId) => {
    try {
      const response = await axios.get(`${API}/teachers/${teacherId}/profile`);
      setProfileData(response.data);
      setIsProfileDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load teacher profile");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        salary: formData.salary ? parseFloat(formData.salary) : null,
      };
      
      if (editingTeacher) {
        await axios.put(`${API}/teachers/${editingTeacher.id}`, submitData);
        toast.success("Teacher updated successfully");
      } else {
        await axios.post(`${API}/teachers`, submitData);
        toast.success("Teacher added successfully");
      }
      fetchData();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this teacher?")) return;
    try {
      await axios.delete(`${API}/teachers/${id}`);
      toast.success("Teacher deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete teacher");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      password: "",
      subjects: [],
      assigned_classes: [],
      salary: "",
      role: "teacher",
    });
    setEditingTeacher(null);
  };

  const openEditDialog = (teacher) => {
    setEditingTeacher(teacher);
    setFormData({
      name: teacher.name,
      email: teacher.email,
      phone: teacher.phone || "",
      password: "",
      subjects: teacher.subjects || [],
      assigned_classes: teacher.assigned_classes || [],
      salary: teacher.salary ? teacher.salary.toString() : "",
      role: "teacher",
    });
    setIsDialogOpen(true);
  };

  const handleSubjectToggle = (subject) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.includes(subject)
        ? prev.subjects.filter(s => s !== subject)
        : [...prev.subjects, subject]
    }));
  };

  const handleClassToggle = (classId) => {
    setFormData(prev => ({
      ...prev,
      assigned_classes: prev.assigned_classes.includes(classId)
        ? prev.assigned_classes.filter(c => c !== classId)
        : [...prev.assigned_classes, classId]
    }));
  };

  const getTeacherClasses = (teacher) => {
    const assignedClassIds = teacher.assigned_classes || [];
    return classes.filter((c) => assignedClassIds.includes(c.id));
  };

  const filteredTeachers = teachers.filter((teacher) =>
    teacher.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    teacher.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (teacher.subjects || []).some(s => s.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="teachers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#1E624A]">Teachers Management</h2>
          <p className="text-sm text-gray-600">View, assign, and manage all teaching staff</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-teacher-btn"
              onClick={resetForm}
              className="bg-[#1E624A] hover:bg-[#164935]"
            >
              <Plus size={20} className="mr-2" />
              Add Teacher
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTeacher ? "Edit Teacher" : "Add New Teacher"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    data-testid="teacher-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="teacher-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    data-testid="teacher-phone-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+252-XX-XXX-XXXX"
                  />
                </div>
                <div>
                  <Label htmlFor="salary">Monthly Salary ($)</Label>
                  <Input
                    id="salary"
                    type="number"
                    step="0.01"
                    data-testid="teacher-salary-input"
                    value={formData.salary}
                    onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                    placeholder="800.00"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="password">
                  Password {editingTeacher ? "(leave blank to keep current)" : "*"}
                </Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="teacher-password-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingTeacher}
                  placeholder={editingTeacher ? "Leave blank to keep current" : "Enter password"}
                />
              </div>
              <div>
                <Label>Subjects *</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {SUBJECTS.map(subject => (
                    <div key={subject} className="flex items-center space-x-2">
                      <Checkbox
                        id={`subject-${subject}`}
                        checked={formData.subjects.includes(subject)}
                        onCheckedChange={() => handleSubjectToggle(subject)}
                      />
                      <label
                        htmlFor={`subject-${subject}`}
                        className="text-sm cursor-pointer"
                      >
                        {subject}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label>Assigned Classes</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {classes.map(cls => (
                    <div key={cls.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`class-${cls.id}`}
                        checked={formData.assigned_classes.includes(cls.id)}
                        onCheckedChange={() => handleClassToggle(cls.id)}
                      />
                      <label
                        htmlFor={`class-${cls.id}`}
                        className="text-sm cursor-pointer"
                      >
                        {cls.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="cancel-teacher-btn"
                  onClick={() => {
                    setIsDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="save-teacher-btn"
                  className="bg-[#1E624A] hover:bg-[#164935]"
                >
                  {editingTeacher ? "Update" : "Add"} Teacher
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl p-4 shadow-md">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <Input
            data-testid="search-teachers-input"
            placeholder="Search teachers by name, subject, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Teachers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTeachers.map((teacher) => {
          const teacherClasses = getTeacherClasses(teacher);
          return (
            <div
              key={teacher.id}
              data-testid={`teacher-card-${teacher.id}`}
              className="bg-white rounded-xl p-6 shadow-md card-hover"
            >
              {/* Teacher Header */}
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 rounded-full bg-[#E4C987] flex items-center justify-center text-[#1E624A] font-bold text-xl">
                  {teacher.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900">{teacher.name}</h3>
                  <p className="text-sm text-gray-600">{teacher.email}</p>
                  <p className="text-sm text-gray-600">{teacher.phone || "N/A"}</p>
                </div>
              </div>

              {/* Subjects */}
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen size={16} className="text-[#1E624A]" />
                  <span className="text-sm font-semibold text-gray-700">Subjects:</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(teacher.subjects || []).length > 0 ? (
                    teacher.subjects.map((subject) => (
                      <span
                        key={subject}
                        className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full"
                      >
                        {subject}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-400">No subjects assigned</span>
                  )}
                </div>
              </div>

              {/* Assigned Classes */}
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <GraduationCap size={16} className="text-[#1E624A]" />
                  <span className="text-sm font-semibold text-gray-700">Classes:</span>
                </div>
                {teacherClasses.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {teacherClasses.map((cls) => (
                      <span
                        key={cls.id}
                        className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full"
                      >
                        {cls.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-gray-400">No classes assigned</span>
                )}
              </div>

              {/* Salary */}
              {teacher.salary && (
                <div className="mb-4 text-sm">
                  <span className="font-semibold text-gray-700">Salary: </span>
                  <span className="text-[#1E624A] font-bold">${teacher.salary.toFixed(2)}/month</span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t border-gray-200">
                <Button
                  data-testid={`view-teacher-${teacher.id}-btn`}
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={() => fetchTeacherProfile(teacher.id)}
                >
                  <Eye size={16} className="mr-1" />
                  View
                </Button>
                <Button
                  data-testid={`edit-teacher-${teacher.id}-btn`}
                  size="sm"
                  variant="outline"
                  onClick={() => openEditDialog(teacher)}
                >
                  <Edit size={16} />
                </Button>
                <Button
                  data-testid={`delete-teacher-${teacher.id}-btn`}
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => handleDelete(teacher.id)}
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredTeachers.length === 0 && (
        <div className="bg-white rounded-xl p-12 text-center text-gray-500">
          No teachers found
        </div>
      )}

      {/* Summary stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-md">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <UserCheck size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Teachers</p>
              <p className="text-2xl font-bold text-gray-900">{teachers.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-md">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
              <UserCheck size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Teachers with Classes</p>
              <p className="text-2xl font-bold text-gray-900">
                {teachers.filter(t => getTeacherClasses(t).length > 0).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-md">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
              <UserCheck size={24} className="text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Unassigned Teachers</p>
              <p className="text-2xl font-bold text-gray-900">
                {teachers.filter(t => getTeacherClasses(t).length === 0).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Teacher Profile Dialog */}
      <Dialog open={isProfileDialogOpen} onOpenChange={setIsProfileDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {profileData && (
            <>
              <DialogHeader>
                <DialogTitle>Teacher Profile</DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                {/* Personal Info */}
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 rounded-full bg-[#E4C987] flex items-center justify-center text-[#1E624A] font-bold text-2xl">
                    {profileData.teacher.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900">{profileData.teacher.name}</h3>
                    <p className="text-gray-600">{profileData.teacher.email}</p>
                    <p className="text-gray-600">{profileData.teacher.phone || "N/A"}</p>
                    {profileData.teacher.salary && (
                      <p className="text-[#1E624A] font-bold mt-2">
                        ${profileData.teacher.salary.toFixed(2)}/month
                      </p>
                    )}
                  </div>
                </div>

                {/* Subjects & Classes */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <BookOpen size={18} className="text-[#1E624A]" />
                      Subjects
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {(profileData.teacher.subjects || []).map((subject) => (
                        <span key={subject} className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full">
                          {subject}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <GraduationCap size={18} className="text-[#1E624A]" />
                      Classes
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {profileData.classes.map((cls) => (
                        <span key={cls.id} className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full">
                          {cls.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Quick Statistics</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <GraduationCap size={24} className="text-blue-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-blue-600">{profileData.stats.total_classes}</p>
                      <p className="text-sm text-gray-600">Classes</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <Users size={24} className="text-green-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-green-600">{profileData.stats.total_students}</p>
                      <p className="text-sm text-gray-600">Students</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                      <BookOpen size={24} className="text-purple-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-purple-600">{profileData.stats.total_assessments}</p>
                      <p className="text-sm text-gray-600">Assessments</p>
                    </div>
                  </div>
                </div>

                {/* Send Message Button */}
                <div className="flex justify-end pt-4 border-t border-gray-200">
                  <Button
                    data-testid="send-message-btn"
                    onClick={() => {
                      setIsProfileDialogOpen(false);
                      window.location.href = `/messages?compose=true&to=${profileData.teacher.id}&name=${encodeURIComponent(profileData.teacher.name)}`;
                    }}
                    className="bg-[#1E624A] hover:bg-[#164935]"
                  >
                    <MessageSquare size={16} className="mr-2" />
                    Send Message
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Teachers;
